import { useCallback, useEffect, useRef, useState } from 'react';
import '../ComplaintsComponent.mobile.css';
import './CustomerDetails.css';

export default function CutomerDetails(props) {
  const { item, getReplacementDetails, handleJiraClick, getAppUserId } = props;

  const [showTickets, setShowTickets] = useState(false);
  const [jiraTicketKey, setJiraTicketKey] = useState(null);
  const [isCreatingJira, setIsCreatingJira] = useState(false);
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [replacementData, setReplacementData] = useState([]);
  const [loadingReplacement, setLoadingReplacement] = useState(false);

  // null = not looked up yet, [] = looked up and no NFA account found.
  const [appUserIds, setAppUserIds] = useState(null);
  const [loadingAppUserId, setLoadingAppUserId] = useState(false);

  const dropdownRef = useRef(null);

  const currentJiraKey = jiraTicketKey || item?.customer?.jira_ticket_id;

  const closeModal = useCallback(() => {
    setShowReplacementModal(false);
    setReplacementData([]);
  }, []);

  const handleReplacementStatus = useCallback(async () => {
    setShowReplacementModal(true);
    setLoadingReplacement(true);

    try {
      const payload = { complaint_id: item.id };
      const response = await getReplacementDetails(payload);
      setReplacementData(response?.data || []);
    } catch (error) {
      alert(error.message || 'Failed to fetch replacement details');
      setShowReplacementModal(false);
    } finally {
      setLoadingReplacement(false);
    }
  }, [getReplacementDetails, item?.id]);

  // The NFA app user id is only obtainable by matching this customer's mobile against the
  // separate NoiseFit user base, so it's fetched on demand rather than stored on the customer
  // (a customer can install the app after raising the ticket, which would leave a stored id
  // permanently wrong).
  const handleGetAppUserId = useCallback(async () => {
    if (!item?.customer?.id) {
      alert('Customer ID is not available for this ticket.');
      return;
    }
    setLoadingAppUserId(true);
    try {
      const response = await getAppUserId(item.customer.id);
      if (response?.success) {
        setAppUserIds(response?.data?.app_user_ids || []);
      } else {
        alert(response?.message || 'Could not fetch the app user id.');
      }
    } catch (error) {
      alert(error.message || 'Failed to fetch app user id');
    } finally {
      setLoadingAppUserId(false);
    }
  }, [getAppUserId, item?.customer?.id]);

  const copyAppUserId = useCallback((appUserId) => {
    navigator.clipboard.writeText(`${appUserId}`);
    alert('App User ID copied!');
  }, []);

  const copyTicket = useCallback((text) => {
    navigator.clipboard.writeText(text);
    alert('Ticket ID copied!');
    setShowTickets(false);
  }, []);

  const handleJiraClickInternal = useCallback(
    async (e, selectedItem) => {
      setIsCreatingJira(true);
      try {
        const response = await handleJiraClick(e, selectedItem);
        if (response?.success) {
          setJiraTicketKey(response?.data?.jiraKey || null);
        }
      } catch (error) {
        alert('Failed to create Jira ticket');
      } finally {
        setIsCreatingJira(false);
      }
    },
    [handleJiraClick]
  );

  const handleShowTickets = useCallback(() => {
    setShowTickets((prev) => !prev);
  }, []);

  const handleOutsideClick = useCallback((e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setShowTickets(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  // Clear the fetched id when the tab is shown for a different customer — otherwise switching
  // tickets without unmounting would keep displaying the previous customer's app user id.
  useEffect(() => {
    setAppUserIds(null);
  }, [item?.customer?.id]);

  const openJiraTicket = useCallback((key) => {
    if (!key) return;
    const jiraUrl = `https://gonoise.atlassian.net/browse/${key}`;
    window.open(jiraUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleDownload = useCallback(async (url) => {
    try {
      const response = await fetch(url, { mode: 'cors' });
      const blob = await response.blob();

      let fileName = 'downloaded-file';
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition && contentDisposition.includes('filename=')) {
        const rawName = contentDisposition.split('filename=')[1];
        fileName = rawName.replace(/['"]/g, '').trim();
      } else {
        const pathName = new URL(url).pathname;
        const last = pathName.substring(pathName.lastIndexOf('/') + 1);
        if (last) {
          fileName = decodeURIComponent(last);
        }
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      const fallback = document.createElement('a');
      fallback.href = url;
      fallback.target = '_blank';
      fallback.rel = 'noreferrer';
      fallback.setAttribute('download', '');
      document.body.appendChild(fallback);
      fallback.click();
      document.body.removeChild(fallback);
    }
  }, []);

  return (
    <div className="heads customer-details-theme">
      <div className="customer-details-container">
        <div className="first">
          <div className="firstbox">
            <span>Name</span>
            <p>{item.customer.name || 'N/A'}</p>
          </div>
          <div className="secondbox">
            <span>Address 1</span>
            <p>{item.customer.address || 'N/A'}</p>
          </div>
          <div className="thirdbox">
            <span>City</span>
            <p>{item.customer.city || 'N/A'}</p>
          </div>
          <div className="thirdbox">
            <span>Customer ID</span>
            <p>{item.customer.id || 'N/A'}</p>
          </div>
          {/* Only rendered where the parent supplies the action (Complaints, Noise Junior and
              Master Buds) — same self-gating convention as Check Pairing History below. */}
          {typeof getAppUserId === 'function' && (
            <div className="thirdbox app_user_id_box">
              <span>App User ID</span>
              {appUserIds === null ? (
                <button
                  type="button"
                  className="app_user_id_button"
                  onClick={handleGetAppUserId}
                  disabled={loadingAppUserId}
                >
                  {loadingAppUserId ? 'Fetching...' : 'Get App User ID'}
                </button>
              ) : appUserIds.length ? (
                <div className="app_user_id_values">
                  {appUserIds.map((appUserId) => (
                    <p key={appUserId}>
                      {appUserId}
                      <button
                        type="button"
                        className="app_user_id_copy"
                        title="Copy App User ID"
                        onClick={() => copyAppUserId(appUserId)}
                      >
                        Copy
                      </button>
                    </p>
                  ))}
                </div>
              ) : (
                <p className="app_user_id_empty">No NoiseFit app account found for this mobile.</p>
              )}
            </div>
          )}
          <div className="app_version">
            {item.customer.app_log && (
              <>
                <span>App Version</span>
                <p>{item.customer.app_version || 'N/A'}</p>
              </>
            )}
          </div>
        </div>

        <div className="second">
          <div className="firstbox">
            <span>Email</span>
            <p>{item.customer.email || 'N/A'}</p>
          </div>
          <div className="secondbox">
            <span>Address 2</span>
            <p>{item.customer.address_2 || 'N/A'}</p>
          </div>
          <div className="thirdbox">
            <span>Pincode</span>
            <p>{item.customer.pincode || 'N/A'}</p>
          </div>
          <div className="platform">
            {item.customer.app_log && (
              <>
                <span>Platform</span>
                <p>{item.customer.platform || 'N/A'}</p>
              </>
            )}
          </div>
          <div className="firmware">
            {item.customer.app_log && (
              <>
                <span>Firmware Version</span>
                <p>{item.customer.firmware || 'N/A'}</p>
              </>
            )}
          </div>
        </div>

        <div className="third">
          <div className="thirdbox" ref={dropdownRef}>
            <span>Tickets history</span>

            <button className="btn btn-sm btn-primary" onClick={handleShowTickets} style={{ marginLeft: '10px' }}>
              Previous Tickets ▾
            </button>

            {showTickets && (
              <div className="ticket-dropdown">
                {(item.customer?.prev_complaints || []).map((ticket, index) => (
                  <div key={ticket.ticket_id || index} className="ticket-item" onClick={() => copyTicket(ticket.ticket_id)}>
                    {ticket.ticket_id}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="firstbox">
            <span>Mobile Number</span>
            <p>{item.customer.mobile || 'N/A'}</p>
          </div>
          <div className="secondbox">
            <span>State</span>
            <p>{item.customer.state_name || 'N/A'}</p>
          </div>
          <div className="problem_des">
            <span>Problem Description</span>
            <p
              data-bs-toggle="tooltip"
              data-bs-placement="bottom"
              className="tooltip-white text-start"
              title={item.problem_description}
            >
              {item.problem_description}
            </p>
          </div>
          <div className="user_agent">
            {item.customer.app_log && (
              <>
                <span>Phone Info</span>
                <p>{item.customer.user_agent || 'N/A'}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {item.product?.product_group_id === 2 && typeof getReplacementDetails === 'function' && (
        <div className="replacement_status_container">
          <button className="replace_button" onClick={handleReplacementStatus}>
            Check Pairing History
          </button>
        </div>
      )}

      {showReplacementModal && (
        <div className="replacement-modal-overlay">
          <div className="replacement-modal-content">
            <div className="modal-header">
              <h5 className="m-0">NFA Pairing Details</h5>
              <button className="close-x" onClick={closeModal}>&times;</button>
            </div>

            <div className="modal-body">
              {loadingReplacement ? (
                <div className="text-center p-3">Loading details...</div>
              ) : (
                <div className="table-responsive">
                  <table className="replacement-table">
                    <thead>
                      <tr>
                        <th>Ticket ID</th>
                        <th>Product Name</th>
                        <th>MAC Address</th>
                        <th>Shopify Order ID</th>
                        <th>isReplaced</th>
                      </tr>
                    </thead>
                    <tbody>
                      {replacementData.length > 0 ? (
                        replacementData.map((data, index) => (
                          <tr key={index}>
                            <td>{data.ticket_id || 'N/A'}</td>
                            <td>{data.product_name}</td>
                            <td>{data.address || 'N/A'}</td>
                            <td>{data.shopify_order_id || 'N/A'}</td>
                            <td>
                              <span className={data.isReplaced ? 'badge-replaced' : 'badge-pending'}>
                                {data.isReplaced ? 'Replaced' : 'Not Replaced'}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center">No records found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="deep_link">
        <div className="app_log">
          {item.customer.app_log && (
            <a
              className="log-download-link"
              href={item.customer.app_log}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault();
                handleDownload(item.customer.app_log);
              }}
            >
              App Logs
              <img
                src="https://cdn.shopify.com/s/files/1/0997/6284/files/Vector_d9268827-dfc7-4bc7-a3de-05e90e95d8fd.png?v=1768451796"
                className="download-link-icon"
                alt="download"
              />
            </a>
          )}
        </div>
        <div className="device_log">
          {item.customer.device_log && (
            <a
              className="log-download-link"
              href={item.customer.device_log}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault();
                handleDownload(item.customer.device_log);
              }}
            >
              Device Logs
              <img
                src="https://cdn.shopify.com/s/files/1/0997/6284/files/Vector_d9268827-dfc7-4bc7-a3de-05e90e95d8fd.png?v=1768451796"
                className="download-link-icon"
                alt="download"
              />
            </a>
          )}
        </div>
      </div>

      <div className="jira-container">
        {item.customer?.app_log && typeof handleJiraClick === 'function' && (
          <>
            <button
              onClick={(e) => handleJiraClickInternal(e, item)}
              disabled={isCreatingJira || !!currentJiraKey}
              className={`jira-btn ${currentJiraKey ? 'btn-success' : 'btn-primary'}`}
            >
              <i className="fab fa-jira jira-icon"></i>
              {isCreatingJira ? 'Creating...' : currentJiraKey ? 'Ticket Created' : 'Create Jira Ticket'}
            </button>

            {currentJiraKey && (
              <div
                className="jira-badge"
                onClick={() => openJiraTicket(currentJiraKey)}
                title="Open Jira ticket"
              >
                Jira Ticket ID: {currentJiraKey}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
